"use client"

import { useState, useEffect, useRef } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import { Connection, VersionedTransaction } from "@solana/web3.js"
import { ArrowUpDown, Loader2, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { CustomSelect } from "./custom-select"
import {
  getSwapQuote,
  createSwapSolForIrmaTransaction,
  createSwapIrmaForSolTransaction,
  getJupiterQuote,
  createJupiterSwapTransaction,
  getRoutedQuote,
  isRoutedSwap,
  getTokenMint,
  TOKEN_DECIMALS,
  IRMA_PER_SOL,
  FEE_BPS
} from "@/lib/swap-client"

// Jupiter only works on mainnet; on devnet only SOL↔IRMA via on-chain contract
const IS_DEVNET = (process.env.NEXT_PUBLIC_SOLANA_NETWORK || "devnet") === "devnet"
const SUPPORTED_TOKENS = IS_DEVNET
  ? (["SOL", "IRMA"] as const)
  : (["SOL", "IRMA", "USDC", "USDT"] as const)

interface SwapFormProps {
  connection: Connection
  onSwapComplete?: () => void
}

export function SwapForm({ connection, onSwapComplete }: SwapFormProps) {
  const { publicKey, signTransaction } = useWallet()
  const [fromToken, setFromToken] = useState<string>("SOL")
  const [toToken, setToToken] = useState<string>("IRMA")
  const [amount, setAmount] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quote, setQuote] = useState<any>(null)
  const [balances, setBalances] = useState<Record<string, number>>({
    SOL: 0, IRMA: 0, USDC: 0, USDT: 0,
  })
  const quoteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (publicKey && connection) {
      loadBalances()
    }
  }, [publicKey, connection])

  const loadBalances = async () => {
    if (!publicKey) return

    try {
      const solBalance = await connection.getBalance(publicKey)
      const newBalances: Record<string, number> = {
        SOL: solBalance / 1e9,
        IRMA: 0, USDC: 0, USDT: 0,
      }

      const { getAssociatedTokenAddress, getAccount } = await import("@solana/spl-token")

      for (const token of ["IRMA", "USDC", "USDT"] as const) {
        try {
          const mint = getTokenMint(token)
          const decimals = TOKEN_DECIMALS[token]
          const ata = await getAssociatedTokenAddress(mint, publicKey)
          const account = await getAccount(connection, ata)
          newBalances[token] = Number(account.amount) / Math.pow(10, decimals)
        } catch {
          newBalances[token] = 0
        }
      }

      setBalances(newBalances)
    } catch (err) {
      console.error("Error loading balances:", err)
    }
  }

  const isContractSwap = (from: string, to: string) =>
    (from === "SOL" && to === "IRMA") || (from === "IRMA" && to === "SOL")

  // Calculate quote with debounce for Jupiter
  useEffect(() => {
    if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current)

    if (!amount || parseFloat(amount) <= 0 || fromToken === toToken) {
      setQuote(null)
      setQuoteLoading(false)
      return
    }

    const inputAmount = parseFloat(amount)

    if (isContractSwap(fromToken, toToken)) {
      const inputToken = fromToken === "SOL" ? "SOL" : "IRMA"
      const quoteData = getSwapQuote(inputToken as "SOL" | "IRMA", inputAmount)
      setQuote(quoteData)
      setQuoteLoading(false)
    } else if (!IS_DEVNET && isRoutedSwap(fromToken, toToken)) {
      // Routed: USDC/USDT ↔ IRMA via SOL intermediate
      setQuoteLoading(true)
      quoteTimerRef.current = setTimeout(async () => {
        try {
          const quoteData = await getRoutedQuote(fromToken, toToken, inputAmount)
          setQuote(quoteData)
        } catch (err) {
          console.error("Routed quote error:", err)
          setQuote(null)
        } finally {
          setQuoteLoading(false)
        }
      }, 500)
    } else if (!IS_DEVNET) {
      // Direct Jupiter: SOL↔USDC, SOL↔USDT, USDC↔USDT
      setQuoteLoading(true)
      quoteTimerRef.current = setTimeout(async () => {
        try {
          const quoteData = await getJupiterQuote(fromToken, toToken, inputAmount)
          setQuote(quoteData)
        } catch (err) {
          console.error("Jupiter quote error:", err)
          setQuote(null)
        } finally {
          setQuoteLoading(false)
        }
      }, 500)
    } else {
      setQuote(null)
      setQuoteLoading(false)
    }

    return () => {
      if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current)
    }
  }, [amount, fromToken, toToken])

  const handleSwap = async () => {
    if (!publicKey || !signTransaction || !amount || !quote) {
      toast.error("Please connect wallet and enter amount")
      return
    }

    if (fromToken === toToken) {
      toast.error("Cannot swap same token")
      return
    }

    setLoading(true)

    try {
      let signature: string

      if (isContractSwap(fromToken, toToken)) {
        // Use on-chain IRMA swap contract
        const irmaMint = getTokenMint("IRMA")
        let transaction

        if (fromToken === "SOL") {
          transaction = await createSwapSolForIrmaTransaction(
            connection, publicKey, parseFloat(amount), irmaMint
          )
        } else {
          transaction = await createSwapIrmaForSolTransaction(
            connection, publicKey, parseFloat(amount), irmaMint
          )
        }

        const signed = await signTransaction(transaction)
        signature = await connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: false, maxRetries: 3,
        })
      } else if (!IS_DEVNET && isRoutedSwap(fromToken, toToken)) {
        // Routed swap: USDC/USDT ↔ IRMA via SOL intermediate (2 transactions)
        const routedQuote = quote as import("@/lib/swap-client").RoutedSwapQuote

        if (routedQuote.route === "token-to-irma") {
          // Step 1: USDC/USDT → SOL via Jupiter
          toast.info("Step 1/2: Swapping " + fromToken + " to SOL via Jupiter...")
          const txBuf = await createJupiterSwapTransaction(
            fromToken, "SOL", parseFloat(amount), publicKey
          )
          const versionedTx = VersionedTransaction.deserialize(txBuf)
          const signed1 = await signTransaction(versionedTx)
          const sig1 = await connection.sendRawTransaction(signed1.serialize(), {
            skipPreflight: false, maxRetries: 3,
          })
          await connection.confirmTransaction(sig1, "confirmed")

          // Step 2: SOL → IRMA via contract
          toast.info("Step 2/2: Swapping SOL to IRMA via contract...")
          const irmaMint = getTokenMint("IRMA")
          const solAmount = routedQuote.intermediarySolAmount
          const contractTx = await createSwapSolForIrmaTransaction(
            connection, publicKey, solAmount, irmaMint
          )
          const signed2 = await signTransaction(contractTx)
          signature = await connection.sendRawTransaction(signed2.serialize(), {
            skipPreflight: false, maxRetries: 3,
          })
        } else {
          // Step 1: IRMA → SOL via contract
          toast.info("Step 1/2: Swapping IRMA to SOL via contract...")
          const irmaMint = getTokenMint("IRMA")
          const contractTx = await createSwapIrmaForSolTransaction(
            connection, publicKey, parseFloat(amount), irmaMint
          )
          const signed1 = await signTransaction(contractTx)
          const sig1 = await connection.sendRawTransaction(signed1.serialize(), {
            skipPreflight: false, maxRetries: 3,
          })
          await connection.confirmTransaction(sig1, "confirmed")

          // Step 2: SOL → USDC/USDT via Jupiter
          toast.info("Step 2/2: Swapping SOL to " + toToken + " via Jupiter...")
          const solAmount = routedQuote.intermediarySolAmount
          const txBuf = await createJupiterSwapTransaction(
            "SOL", toToken, solAmount, publicKey
          )
          const versionedTx = VersionedTransaction.deserialize(txBuf)
          const signed2 = await signTransaction(versionedTx)
          signature = await connection.sendRawTransaction(signed2.serialize(), {
            skipPreflight: false, maxRetries: 3,
          })
        }
      } else if (!IS_DEVNET) {
        // Direct Jupiter: SOL↔USDC, SOL↔USDT, USDC↔USDT
        const txBuf = await createJupiterSwapTransaction(
          fromToken, toToken, parseFloat(amount), publicKey
        )
        const versionedTx = VersionedTransaction.deserialize(txBuf)
        const signed = await signTransaction(versionedTx)
        signature = await connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: false, maxRetries: 3,
        })
      } else {
        throw new Error("USDC/USDT swaps are not available on devnet")
      }

      await connection.confirmTransaction(signature!, "confirmed")

      toast.success(`Swap successful! Transaction: ${signature!.slice(0, 8)}...`)

      setAmount("")
      setQuote(null)
      await loadBalances()
      onSwapComplete?.()
    } catch (err: any) {
      console.error("Swap error:", err)
      toast.error(err.message || "Swap failed")
    } finally {
      setLoading(false)
    }
  }

  const swapTokens = () => {
    const temp = fromToken
    setFromToken(toToken)
    setToToken(temp)
    setQuote(null)
  }

  const handleFromTokenChange = (value: string) => {
    setFromToken(value)
    if (value === toToken) {
      const other = SUPPORTED_TOKENS.find(t => t !== value) || "SOL"
      setToToken(other)
    }
  }

  const handleToTokenChange = (value: string) => {
    setToToken(value)
    if (value === fromToken) {
      const other = SUPPORTED_TOKENS.find(t => t !== value) || "IRMA"
      setFromToken(other)
    }
  }

  const outputAmount = quote ? quote.outputAmount.toFixed(6) : "0"
  const isJupiterSwap = !isContractSwap(fromToken, toToken) && !isRoutedSwap(fromToken, toToken)
  const isRouted = isRoutedSwap(fromToken, toToken)

  const TOKEN_OPTIONS = SUPPORTED_TOKENS.map(token => ({
    value: token,
    label: token,
    icon: (
      <div className="w-7 h-7 rounded-full bg-[#25e4e8] flex items-center justify-center text-black font-bold text-sm">
        {token[0]}
      </div>
    ),
  }))

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        {/* Header */}
        <div className="px-1 mb-3">
          <div className="text-sm text-[#8B949E] font-medium">
            {isRouted
              ? "Routed via SOL (Jupiter + Contract)"
              : isJupiterSwap
                ? "Powered by Jupiter Aggregator"
                : `Rate: 1 SOL = ${IRMA_PER_SOL} IRMA | Fee: ${FEE_BPS / 100}%`
            }
          </div>
        </div>

        {/* Swap Body */}
        <div className="space-y-1.5 relative">
          {/* From Token Input */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 pb-5 border border-transparent focus-within:border-white/10 transition group">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm text-[#8B949E] font-medium ml-1">From</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#8B949E]">
                  Balance: {(balances[fromToken] ?? 0).toFixed(4)} {fromToken}
                </span>
                <button
                  onClick={loadBalances}
                  disabled={loading || !publicKey}
                  className="h-4 w-4 p-0 text-[#8B949E] hover:text-white transition-colors"
                  title="Refresh balance"
                >
                  <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <div className="relative">
                <CustomSelect
                  value={fromToken}
                  onValueChange={handleFromTokenChange}
                  options={TOKEN_OPTIONS}
                  triggerClassName="flex items-center gap-2.5 bg-[#13161C] hover:bg-[#1A1D24] py-2 pl-2 pr-3.5 rounded-full border border-white/5 transition shrink-0 w-auto min-w-[120px] font-bold text-lg text-white tracking-wide"
                  contentClassName="bg-[#13161C] border border-white/10 rounded-lg shadow-xl overflow-hidden"
                  itemClassName="px-3 py-2 text-white hover:bg-[#1C1F26] transition cursor-pointer"
                />
              </div>
              <div className="flex-grow flex flex-col items-end">
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-transparent text-right text-[32px] font-semibold outline-none text-white placeholder-[#3b4153] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-1">
              <button
                onClick={() => setAmount((balances[fromToken] ?? 0).toFixed(6))}
                className="text-xs text-[#8B949E] hover:text-white transition-colors font-medium"
              >
                Max
              </button>
            </div>
          </div>

          {/* Swap Flip Button */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <button
              onClick={swapTokens}
              className="bg-[#1C1F26] border-4 border-[#13161C] hover:bg-[#252830] p-2 rounded-full text-white transition transform active:scale-95 group"
            >
              <ArrowUpDown className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
            </button>
          </div>

          {/* To Token Input */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 pb-5 border border-transparent focus-within:border-white/10 transition">
            <label className="block text-sm text-[#8B949E] font-medium mb-3 ml-1">To</label>
            <div className="flex justify-between items-center gap-4">
              <div className="relative">
                <CustomSelect
                  value={toToken}
                  onValueChange={handleToTokenChange}
                  options={TOKEN_OPTIONS}
                  triggerClassName="flex items-center gap-2.5 bg-[#13161C] hover:bg-[#1A1D24] py-2 pl-2 pr-3.5 rounded-full border border-white/5 transition shrink-0 w-auto min-w-[120px] font-bold text-lg text-white tracking-wide"
                  contentClassName="bg-[#13161C] border border-white/10 rounded-lg shadow-xl overflow-hidden"
                  itemClassName="px-3 py-2 text-white hover:bg-[#1C1F26] transition cursor-pointer"
                />
              </div>
              <div className="flex-grow flex flex-col items-end relative">
                <input
                  type="text"
                  value={quoteLoading ? "" : outputAmount}
                  readOnly
                  placeholder={quoteLoading ? "Getting quote..." : "0"}
                  className="w-full bg-transparent text-right text-[32px] font-semibold outline-none text-white placeholder-[#3b4153]"
                />
                {quoteLoading && (
                  <Loader2 className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-[#8B949E]" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quote Details */}
        {quote && (
          <div className="px-4 py-2 text-xs text-[#8B949E] space-y-1">
            {isRouted ? (
              <>
                <div className="flex justify-between">
                  <span>Rate:</span>
                  <span>1 {fromToken} = {quote.rate.toFixed(6)} {toToken}</span>
                </div>
                <div className="flex justify-between">
                  <span>Route:</span>
                  <span>{fromToken} → SOL → {toToken} (2 txns)</span>
                </div>
                <div className="flex justify-between">
                  <span>Intermediate:</span>
                  <span>~{quote.intermediarySolAmount?.toFixed(6)} SOL</span>
                </div>
                {quote.priceImpact > 0 && (
                  <div className="flex justify-between">
                    <span>Price Impact:</span>
                    <span>{(quote.priceImpact * 100).toFixed(2)}%</span>
                  </div>
                )}
              </>
            ) : isJupiterSwap ? (
              <>
                <div className="flex justify-between">
                  <span>Rate:</span>
                  <span>1 {fromToken} = {quote.rate.toFixed(6)} {toToken}</span>
                </div>
                {quote.priceImpact > 0 && (
                  <div className="flex justify-between">
                    <span>Price Impact:</span>
                    <span>{(quote.priceImpact * 100).toFixed(2)}%</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Fee:</span>
                  <span>{quote.fee.toFixed(6)} {toToken}</span>
                </div>
                <div className="flex justify-between">
                  <span>Rate:</span>
                  <span>1 SOL = {quote.rate} IRMA</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Error Message */}
        {fromToken === toToken && (
          <div className="px-4 py-2">
            <p className="text-sm text-red-400 text-center">Cannot swap same token</p>
          </div>
        )}

        {/* Swap Button */}
        <div className="mt-3">
          {!publicKey ? (
            <div className="text-center">
              <p className="text-sm text-[#8B949E] mb-3">Connect your wallet to swap</p>
              <button className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98]">
                Connect Wallet
              </button>
            </div>
          ) : (
            <button
              onClick={handleSwap}
              disabled={loading || !amount || !quote || parseFloat(amount) <= 0 || fromToken === toToken || quoteLoading}
              className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Swapping...
                </>
              ) : (
                "Swap"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
